<template>
    <div class="quickpay-deadlines" :class="rootClasses">
        <div class="quickpay-deadlines__header">
            <span class="quickpay-deadlines__title typographies-paragraph-medium-bold">
                {{ computedTitle }}
            </span>
        </div>
        <div class="quickpay-deadlines__preview">
            <div
                v-for="deadline in firstTwoDeadlines"
                :key="deadline.id"
                class="quickpay-deadlines__item"
                :class="getItemClasses(deadline)"
                @click="handleDeadlineClick(deadline)"
            >
                <div class="quickpay-deadlines__item-main">
                    <CompassRadio
                        v-if="selectable"
                        :modelValue="deadline.id"
                        name="deadline-selection"
                        :showLabel="false"
                        :checked="selectedDeadlineId === deadline.id"
                        :disabled="deadline.step === 'paid'"
                        class="quickpay-deadlines__item-radio"
                        @change="() => handleDeadlineClick(deadline)"
                        @click.stop
                    />
                    <span
                        class="quickpay-deadlines__item-date typographies-paragraph-medium-regular"
                        :class="getDateClasses(deadline)"
                    >
                        {{ formatDate(deadline.date) }}
                    </span>
                    <CompassPill
                        v-if="getDeadlineStatus(deadline)"
                        v-bind="getDeadlineStatus(deadline)"
                        :size="CompassEnums.PillSize.SMALL"
                        class="quickpay-deadlines__item-tag"
                    />
                </div>
                <span
                    class="quickpay-deadlines__item-amount typographies-paragraph-medium-black"
                    :class="getAmountClasses(deadline)"
                >
                    {{ formatAmount(deadline.amount_total) }}
                </span>
            </div>
        </div>
        <button
            v-if="hasMoreDeadlines"
            class="quickpay-deadlines__toggle"
            type="button"
            @click="toggleDeadlines"
        >
            <span class="quickpay-deadlines__toggle-text typographies-paragraph-medium-regular">
                {{ translations.viewAll }}
            </span>
            <CompassIcon
                v-if="!isExpanded"
                icon="caret-right-fill"
                :size="CompassEnums.IconSize.SMALL"
                class="quickpay-deadlines__toggle-icon"
            />
            <CompassIcon
                v-else
                icon="caret-down-fill"
                :size="CompassEnums.IconSize.SMALL"
                class="quickpay-deadlines__toggle-icon"
            />
        </button>
        <div class="quickpay-deadlines__scrollable">
            <transition name="list-slide">
                <div v-if="isExpanded" class="quickpay-deadlines__wrapper">
                    <div class="quickpay-deadlines__list">
                        <div
                            v-for="deadline in remainingDeadlines"
                            :key="deadline.id"
                            class="quickpay-deadlines__item"
                            :class="getItemClasses(deadline)"
                            @click="handleDeadlineClick(deadline)"
                        >
                            <div class="quickpay-deadlines__item-main">
                                <CompassRadio
                                    v-if="selectable"
                                    :modelValue="deadline.id"
                                    name="deadline-selection"
                                    :showLabel="false"
                                    :checked="selectedDeadlineId === deadline.id"
                                    :disabled="deadline.step === 'paid'"
                                    class="quickpay-deadlines__item-radio"
                                    @change="() => handleDeadlineClick(deadline)"
                                    @click.stop
                                />
                                <span
                                    class="quickpay-deadlines__item-date typographies-paragraph-medium-regular"
                                    :class="getDateClasses(deadline)"
                                >
                                    {{ formatDate(deadline.date) }}
                                </span>
                                <CompassPill
                                    v-if="getDeadlineStatus(deadline)"
                                    v-bind="getDeadlineStatus(deadline)"
                                    :size="CompassEnums.PillSize.SMALL"
                                    class="quickpay-deadlines__item-tag"
                                />
                            </div>
                            <span
                                class="quickpay-deadlines__item-amount typographies-paragraph-medium-black"
                                :class="getAmountClasses(deadline)"
                            >
                                {{ formatAmount(deadline.amount_total) }}
                            </span>
                        </div>
                    </div>
                </div>
            </transition>
            <transition name="confirmation-block">
                <div v-if="selectable && selectedDeadline" class="quickpay-deadlines__confirmation">
                    <div class="quickpay-deadlines__confirmation-content">
                        <div class="quickpay-deadlines__confirmation-info">
                            <span
                                class="quickpay-deadlines__confirmation-amount typographies-paragraph-large-black"
                            >
                                {{ formatAmount(selectedDeadline.amount_due) }}
                            </span>
                            <span
                                class="quickpay-deadlines__confirmation-date typographies-paragraph-small-regular"
                            >
                                {{ translations.deadlineOf }}
                                {{ formatDate(selectedDeadline.date) }}
                            </span>
                        </div>
                        <CompassButton
                            :type="CompassEnums.ButtonType.PRIMARY_CONTRAST"
                            :label="translations.paySelection"
                            @click="handlePayClick"
                        />
                    </div>
                </div>
            </transition>
            <template v-if="selectable">
                <div class="quickpay-deadlines__divider typographies-paragraph-medium-bold">
                    {{ translations.orDivider }}
                </div>

                <div class="quickpay-deadlines__pay-whole">
                    <CompassButton
                        :type="CompassEnums.ButtonType.TERTIARY"
                        :label="translations.payWholeDocument"
                        @click="handlePayWholeDocument"
                    />
                </div>
            </template>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        deadlines: {
            type: Array,
            required: true,
        },
        currency: {
            type: String,
            required: true,
        },
        selectable: {
            type: Boolean,
            default: false,
        },
        preselectedDeadlineId: {
            type: Number,
            default: null,
        },
        autoExpand: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['pay-deadline', 'pay-whole-document'],
    data() {
        return {
            isExpanded: false,
            selectedDeadlineId: null,
            translations: {
                title: this.$t.__('quickpay.QuickpayDeadlines.title'),
                selectTitle: this.$t.__('quickpay.QuickpayDeadlines.selectTitle'),
                late: this.$t.__('common.labels.isLate'),
                paid: this.$t.__('common.labels.paid'),
                viewAll: this.$t.__('quickpay.QuickpayDeadlines.viewAll'),
                paySelection: this.$t.__('quickpay.QuickpayDeadlines.paySelection'),
                payWholeDocument: this.$t.__('quickpay.QuickpayDeadlines.payWholeDocument'),
                deadlineOf: this.$t.__('quickpay.QuickpayDeadlines.deadlineOf'),
                orDivider: this.$t.__('common.labels.or').toUpperCase(),
            },
        };
    },
    computed: {
        userLocale() {
            const locale = window.App?.locale || 'fr_FR';
            return locale.split('.')[0].replace('_', '-');
        },
        computedTitle() {
            return this.selectable ? this.translations.selectTitle : this.translations.title;
        },
        sortedDeadlines() {
            return [...this.deadlines].sort((a, b) => {
                const aIsLate = this.isLate(a.date) && a.step !== 'paid';
                const bIsLate = this.isLate(b.date) && b.step !== 'paid';
                const aIsPaid = a.step === 'paid';
                const bIsPaid = b.step === 'paid';
                // Late deadlines first, then paid deadlines, then by date
                if (aIsLate && !bIsLate) return -1;
                if (!aIsLate && bIsLate) return 1;
                if (aIsPaid && !bIsPaid) return 1;
                if (!aIsPaid && bIsPaid) return -1;
                return a.date - b.date;
            });
        },
        firstTwoDeadlines() {
            return this.sortedDeadlines.slice(0, 2);
        },
        remainingDeadlines() {
            return this.sortedDeadlines.slice(2);
        },
        hasMoreDeadlines() {
            return this.sortedDeadlines.length > 2;
        },
        selectedDeadline() {
            return this.sortedDeadlines.find((deadline) => deadline.id === this.selectedDeadlineId);
        },
        rootClasses() {
            return {
                'quickpay-deadlines--selectable': this.selectable,
            };
        },
    },
    mounted() {
        if (this.selectable) {
            this.selectedDeadlineId = this.preselectedDeadlineId;
            this.isExpanded = this.autoExpand;
        }
    },
    methods: {
        toggleDeadlines() {
            this.isExpanded = !this.isExpanded;
        },
        formatDate(timestamp) {
            const date = new Date(timestamp * 1000);
            return new Intl.DateTimeFormat(this.userLocale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            }).format(date);
        },
        formatAmount(amount) {
            const numericAmount = parseFloat(amount);
            return new Intl.NumberFormat(this.userLocale, {
                style: 'currency',
                currency: this.currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(numericAmount);
        },
        isLate(timestamp) {
            const deadlineDate = new Date(timestamp * 1000);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return deadlineDate < today;
        },
        getDeadlineStatus(deadline) {
            switch (deadline.step) {
                case 'paid':
                    return {
                        label: this.translations.paid,
                        color: 'green',
                        icon: 'check',
                    };
                case 'due':
                case 'payinprogress':
                    if (this.isLate(deadline.date)) {
                        return {
                            label: this.translations.late,
                            color: 'orange',
                            icon: 'clock',
                        };
                    }
                    return null;
                default:
                    return null;
            }
        },
        getItemClasses(deadline) {
            return {
                'quickpay-deadlines__item--selectable': this.selectable,
                'quickpay-deadlines__item--disabled': this.selectable && deadline.step === 'paid',
                'quickpay-deadlines__item--selected':
                    this.selectable && this.selectedDeadlineId === deadline.id,
            };
        },
        getDateClasses(deadline) {
            return {
                'quickpay-deadlines__item-date--paid': deadline.step === 'paid',
            };
        },
        getAmountClasses(deadline) {
            return {
                'quickpay-deadlines__item-amount--paid': deadline.step === 'paid',
            };
        },
        handleDeadlineClick(deadline) {
            if (!this.selectable || deadline.step === 'paid') {
                return;
            }
            this.selectedDeadlineId = deadline.id;
        },
        handlePayClick() {
            if (this.selectedDeadline) {
                this.$emit('pay-deadline', this.selectedDeadline);
            }
        },
        handlePayWholeDocument() {
            this.$emit('pay-whole-document');
        },
    },
};
</script>

<style lang="scss" scoped>
.quickpay-deadlines {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2);

    &--selectable {
        .quickpay-deadlines__preview,
        .quickpay-deadlines__list {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);
        }
    }
    &__header {
        display: flex;
        align-items: center;
    }
    &__title {
        color: var(--colors-common-text-standard-default);
    }
    &__preview {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: var(--spacing-1);
    }
    &__scrollable {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-2);
    }
    &__toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-0);
        padding: var(--spacing-2);
        background: none;
        border: none;
        cursor: pointer;
        align-self: center;
        &-text {
            color: var(--colors-common-text-subtle-default);
        }
        &-icon {
            color: var(--colors-common-text-subtle-default);
        }
        &:hover {
            .quickpay-deadlines__toggle-text {
                text-decoration: underline;
            }
        }
    }
    &__wrapper {
        display: grid;
        overflow: hidden;
    }
    &__list {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: var(--spacing-1);
        background: var(--colors-common-background-default);
        z-index: 10;
        min-height: 0;
    }
    &__item {
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--spacing-2) var(--spacing-3);
        transition: all 0.2s ease;
        &--selectable {
            background: var(--colors-common-background-default);
            border-radius: var(--spacing-1);
            box-shadow: 0px 0px 9px 0px rgba(143, 143, 143, 0.17);
            cursor: pointer;
            &:hover {
                box-shadow: 2px 0px 10px 3px rgba(0, 0, 0, 0.12);
            }
        }
        &--selected {
            box-shadow: 2px 0px 10px 3px rgba(0, 0, 0, 0.12);
        }
        &--disabled {
            cursor: not-allowed;
            &:hover {
                box-shadow: 0px 0px 9px 0px rgba(143, 143, 143, 0.17);
            }
        }
        &-radio {
            flex-shrink: 0;
        }
        &-main {
            display: flex;
            align-items: center;
            gap: var(--spacing-2);
        }
        &-date {
            color: var(--colors-common-text-subtle-default);
            &--paid {
                color: var(--colors-common-text-standard-disabled-light);
            }
        }
        &-tag {
            flex-shrink: 0;
        }
        &-amount {
            color: var(--colors-common-text-standard-caption);
            text-align: end;
            .quickpay-deadlines--selectable & {
                color: var(--colors-common-text-standard-default);
            }
            &--paid {
                color: var(--colors-common-text-standard-disabled-light);
            }
        }
    }
    &__confirmation {
        position: sticky;
        bottom: var(--spacing-1);
        left: 0;
        right: 0;
        background: var(--colors-common-background-darkest-default);
        border-radius: var(--spacing-1);
        z-index: 100;
        padding: var(--spacing-2) var(--spacing-4);
        &::before {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            bottom: 100%;
            height: 100%;
            background: linear-gradient(180deg, rgba(255, 255, 255, 0) 42.62%, #ffffff 100%);
            pointer-events: none;
            z-index: -1;
        }
        @media (min-width: 1024px) {
            width: 100%;
            max-width: 400px;
        }
        &-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--spacing-3);
            max-width: 400px;
            margin: 0 auto;

            @media (min-width: 1024px) {
                max-width: none;
            }
        }
        &-info {
            display: flex;
            flex-direction: column;
        }
        &-amount {
            color: var(--colors-common-text-contrast-default);
        }

        &-date {
            color: var(--colors-common-text-contrast-default);
        }
    }
    &__divider {
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--colors-common-text-standard-caption);

        &::before,
        &::after {
            content: '';
            flex: 1 1 0;
            height: 1px;
            background: var(--colors-common-text-standard-caption);
        }
        &::before {
            margin-right: var(--spacing-3);
        }
        &::after {
            margin-left: var(--spacing-3);
        }
    }
    &__pay-whole {
        display: flex;
        justify-content: center;
    }
}

.confirmation-block-enter-active {
    transition:
        transform 300ms ease-out 10ms,
        opacity 300ms ease-out 10ms;
}
.confirmation-block-leave-active {
    transition:
        transform 300ms ease-out,
        opacity 300ms ease-out;
}
.confirmation-block-enter-from {
    opacity: 0;
    transform: translateY(100px);
}
.confirmation-block-leave-to {
    opacity: 0;
    transform: translateY(100px);
}
.list-slide-enter-active,
.list-slide-leave-active {
    transition: grid-template-rows 350ms ease;
}
.list-slide-enter-from,
.list-slide-leave-to {
    grid-template-rows: 0fr;
}
.list-slide-enter-to,
.list-slide-leave-from {
    grid-template-rows: 1fr;
}
</style>
